

















































































































































































































































































































































import { defineComponent, computed, PropType, watch, ref } from '@vue/composition-api';

import * as d3 from 'd3-scale-chromatic';

import store from '@/store';
import {
  MotionScoreValueToColor,
  MotionScoreValueToText,
  ScoreClassDefinition,
  MotionScoreValue,
  ScoreRange,
  ScoreSet,
  SimpleFrameData,
  StudyPainScore,
  MotionScoreMapToNumber,
  MotionScoreNumberToMap,
  FrameNumberToTime,
} from '@/types';
import actions from '@/controller/actions';
import { toggleVisibleMode, visibleModes } from '@/controller/state';
import { snackbar } from '@/components/GlobalSnackbar';
import { prompt } from '@/components/Prompt';

function isSetType(scoreClassType: string): boolean {
  return /\w+-set/.test(scoreClassType);
}

function isRangeType(scoreClassType: string): boolean {
  return /\w+-ranges/.test(scoreClassType);
}

function isIntegerType(scoreClassType: string): boolean {
  return /\w*integer\w*/.test(scoreClassType);
}

export default defineComponent({
  name: 'Scorer',
  props: {
    imageData: {
      type: Array as PropType<SimpleFrameData[]>,
      required: true,
    },
  },
  setup(props) {
    const currentStudyType = computed(() => store.state.dataset.studyType);
    const currentFrameIndex = computed(() => store.state.dataset.currentFrameIndex);
    const currentAnnotations = computed(() => store.getters.dataset.currentFrameAnnotations);
    const currentPainScores = computed(() => store.getters.dataset.currentPainScore);
    const currentFrame = computed(() => store.getters.dataset.currentFrame);
    const scoreClasses = computed(() => store.getters.application.scoreClasses);
    const isScorerOrAnnotator = computed(() => store.getters.isScorerOrAnnotator);
    const isSupervisor = computed(() => store.getters.isSupervisor);
    const drawingAnnotation = computed(() => store.state.dataset.drawingAnnotation);
    const annotationsVisible = computed(() => !!visibleModes.value.length);
    const frameConfirmed = computed(
      () => currentFrame.value && currentFrame.value.status === 'confirmed',
    );

    function startDrawingAnnotation() {
      actions.setDrawingAnnotation(!drawingAnnotation.value);
    }

    async function deleteFrame() {
      if (currentFrameIndex.value === null) { return; }

      const index = currentFrameIndex.value;
      const choice = await prompt.open({
        title: 'Confirm',
        message: 'Do you want to delete this frame?',
        positiveText: 'Yes',
        negativeText: 'No',
      });

      if (choice) {
        try {
          // delete on server
          await store.dispatch.dataset.deleteFrame(index);
          // delete locally
          actions.deleteFrame(index);

          // delete success popup
          snackbar.dispatch({
            text: 'Frame Successfully Deleted',
            buttonIcon: 'mdi-close',
            options: {
              top: true,
              color: 'success',
            },
          });
        } catch {
          // delete error popup
          snackbar.dispatch({
            text: 'Error deleting frame. See console for errors.',
            buttonIcon: 'mdi-close',
            options: {
              top: true,
              color: 'error',
            },
          });
        }
      }
    }

    async function saveFrame() {
      try {
        // save success popup
        await store.dispatch.dataset.saveCurrentFrame();
        snackbar.dispatch({
          text: 'Frame Successfully Saved',
          buttonIcon: 'mdi-close',
          options: {
            top: true,
            color: 'success',
          },
        });
      } catch {
        // save error popup
        snackbar.dispatch({
          text: 'Error saving frame. See console for errors.',
          buttonIcon: 'mdi-close',
          options: {
            top: true,
            color: 'error',
          },
        });
      }
    }

    /**
     * Ensure that a study type has a default score value for all of its score classes
     * FIXME: This logic is split half here, half in setCurrentFramePainScore.
     * FIXME: Ideally contain all of this in setCurrentFramePainScore.
     * TODO: This may not behave properly with new range types
     * @param studyType The study type to ensure exists.
     */
    function ensureStudyTypeScore(studyType: string) {
      if (studyType !== 'pain-mgs' && studyType !== 'pain-binary') return;
      const score = currentFrame.value?.score;

      if (
        score === undefined
        || score[studyType] !== undefined
        || scoreClasses.value === null
      ) {
        return;
      }

      const newScoreMap: StudyPainScore = scoreClasses.value.reduce(
        (map, scoreClass) => ({
          ...map,
          [scoreClass.class]: scoreClass.value[0],
        }),
        {},
      );

      Object.keys(newScoreMap).forEach((scoreClass) => {
        store.dispatch.dataset.setCurrentFramePainScore({
          scoreClass,
          score: newScoreMap[scoreClass],
        });
      });
    }

    const studyTypes = computed(() => store.getters.application.studyTypes);
    const studyType = computed({
      get: () => store.state.application.selectedStudyType,
      set(selectedStudyType: string) {
        store.commit.application.setSelectedStudyType(selectedStudyType);
        ensureStudyTypeScore(selectedStudyType);
      },
    });

    /*
      Necessary if the frame changes and the new current frame doesn't
      have an score entry for the selected study type
    */
    watch(currentFrame, () => {
      ensureStudyTypeScore(studyType.value);
    });

    const scoreClassesToValues = computed(
      () => currentFrame.value?.score['pain-mgs'],
    );

    /**
     * This is only called on score fields that are sets of integers
     */
    function incrementDiscreteScore(scoreClass: ScoreClassDefinition) {
      if (!scoreClasses.value) {
        console.error('No defined score classes!');
        return;
      }

      const allowedValues = scoreClass.value as ScoreSet;
      const currentScore = scoreClassesToValues.value
        ? scoreClassesToValues.value[scoreClass.class]
        : null;

      // If doesn't exist, set to -1 so it gets incremented to zero below
      const currentScoreIndex = currentScore === null ? -1 : allowedValues.indexOf(currentScore);
      const score = allowedValues[(currentScoreIndex + 1) % allowedValues.length];

      const payload = {
        scoreClass: scoreClass.class,
        score,
      };
      store.dispatch.dataset.setCurrentFramePainScore(payload);
    }

    function selectMotionClass(motionScore: MotionScoreValue) {
      const motionActionMap = MotionScoreNumberToMap(motionScore);
      const payload = { actions: motionActionMap };
      store.dispatch.dataset.setCurrentFrameMotionScore(payload);
    }

    function scoreValueInRange(
      scoreClass: ScoreClassDefinition,
      value: number,
    ): string | undefined {
      const valueRanges = scoreClass.value as ScoreRange;
      const failed = valueRanges.every(
        ([lower, upper]) => value < lower || value > upper,
      );

      if (failed) {
        return `Value not in any of allowed ranges: ${valueRanges}`;
      }

      return undefined;
    }

    function scoreValueInSet(scoreClass: ScoreClassDefinition, value: number): string | undefined {
      const valueRanges = scoreClass.value as ScoreSet;
      const failed = !valueRanges.includes(value);

      if (failed) {
        return `Value not one of allowed values: ${valueRanges}`;
      }

      return undefined;
    }

    const scoreClassesToErrors = computed((): {[className: string]: string} | undefined => {
      if (scoreClassesToValues.value === undefined) {
        return {};
      }

      return scoreClasses.value?.reduce((cur, scoreClass) => {
        const value = scoreClassesToValues.value?.[scoreClass.class];
        if (value === undefined) {
          return cur;
        }

        let error: string | undefined;

        if (isRangeType(scoreClass.type)) {
          error = scoreValueInRange(scoreClass, value);
        } else if (isSetType(scoreClass.type)) {
          error = scoreValueInSet(scoreClass, value);
        }

        return { ...cur, [scoreClass.class]: error };
      }, {});
    });

    function setScore(scoreClass: ScoreClassDefinition, value: number) {
      const payload = {
        scoreClass: scoreClass.class,
        score: value,
      };
      store.dispatch.dataset.setCurrentFramePainScore(payload);
    }

    function setStartTime(minute: number, second: number) {
      const payload = { startMinute: second >= 60 ? minute + 1 : minute, startSecond: second >= 60 ? second - 60 : second };
      store.dispatch.dataset.setCurrentFrameMotionStartFrame(payload);
    }

    function setEndTime(minute: number, second: number) {
      const payload = { endMinute: second >= 60 ? minute + 1 : minute, endSecond: second >= 60 ? second - 60 : second };
      store.dispatch.dataset.setCurrentFrameMotionEndFrame(payload);
    }

    const scoreErrors = computed(() => (!!Object.values(scoreClassesToErrors.value || {}).filter((x) => !!x).length));
    const frameInvalid = computed(() => !props.imageData || scoreErrors.value);
    const frameInvalidOrConfirmed = computed(() => frameInvalid.value || frameConfirmed.value);
    const frameFlagged = computed(() => currentFrame.value?.status === 'flagged');

    const showConfirmButton = computed(() => store.getters.isSupervisor);
    function confirm() {
      if (currentFrameIndex.value === null) { return; }
      store.dispatch.dataset.setFrameStatus({ index: currentFrameIndex.value, status: 'confirmed' });
    }
    function unconfirm() {
      if (currentFrameIndex.value === null) { return; }
      store.dispatch.dataset.setFrameStatus({ index: currentFrameIndex.value, status: 'pending' });
    }

    function flag() {
      if (currentFrameIndex.value === null) { return; }
      store.dispatch.dataset.setFrameStatus({ index: currentFrameIndex.value, status: 'flagged' });
    }
    function unflag() {
      if (currentFrameIndex.value === null) { return; }
      store.dispatch.dataset.setFrameStatus({ index: currentFrameIndex.value, status: 'pending' });
    }

    function clearScores() {
      const current = currentPainScores.value;
      const noScores = current !== null && Object.keys(current).every((key) => current[key] === -1);
      if (noScores || currentFrameIndex.value === null) { return; }

      store.dispatch.dataset.clearScore(currentFrameIndex.value);
    }

    function clearAnnotations() {
      const current = currentAnnotations.value;
      if (current === null || current.length === 0 || currentFrameIndex.value === null) { return; }
      actions.clearAnnotations(currentFrameIndex.value);
    }

    const dataConsoleOpen = computed(() => store.state.application.showDataConsole);
    function toggleDataConsole() {
      store.commit.application.setShowDataConsole(
        !dataConsoleOpen.value,
      );
    }

    // Motion
    const currentFrameMotionScore = computed(() => store.getters.dataset.currentFrame?.motion_prediction?.predicted_class);
    const currentFrameMotionAnnotationScore = computed(() => MotionScoreMapToNumber(store.getters.dataset.currentFrame?.score?.motion?.actions));
    const currentFrameMotionText = computed(() => MotionScoreValueToText(currentFrameMotionScore.value));
    const currentFrameMotionAnnotationText = computed(() => MotionScoreValueToText(currentFrameMotionAnnotationScore.value));
    const currentFrameMotionColor = computed(() => MotionScoreValueToColor(currentFrameMotionScore.value));
    const currentFrameMotionAnnotationColor = computed(() => MotionScoreValueToColor(currentFrameMotionAnnotationScore.value));
    const currentFrameMotionStartFrame = computed(() => store.getters.dataset.currentFrame?.score?.motion?.start_frame || 0);
    const currentFrameMotionEndFrame = computed(() => store.getters.dataset.currentFrame?.score?.motion?.end_frame || 0);
    const frameRate = computed(() => store.getters.dataset.currentFrame?.reference.fps || 30);
    const numberOfFrames = computed(() => store.getters.dataset.currentFrame?.reference.number_of_frames || 0);
    const startTime = FrameNumberToTime(currentFrameMotionStartFrame.value, frameRate.value);
    const endTime = FrameNumberToTime(currentFrameMotionEndFrame.value, frameRate.value);
    const editStartMinute = ref<number | null>(startTime.minutes);
    const editStartSecond = ref<number | null>(startTime.seconds);
    const editEndMinute = ref<number | null>(endTime.minutes);
    const editEndSecond = ref<number | null>(endTime.seconds);
    const min0Max59Rule = (val: number) => (val >= 0 && val <= 59) || 'the number is out of range';
    const items = [
      {
        score: 0,
        text: 'Sleeping',
        color: d3.schemeCategory10[0],
      },
      {
        score: 1,
        text: 'Awake',
        color: d3.schemeCategory10[2],
      },
      {
        score: 2,
        text: 'Labored',
        color: d3.schemeCategory10[1],
      },
      {
        score: 3,
        text: 'Rearing',
        color: d3.schemeCategory10[3],
      },
      {
        score: 4,
        text: 'Sniffing',
        color: d3.schemeCategory10[4],
      },
      {
        score: 5,
        text: 'Grooming',
        color: d3.schemeCategory10[5],
      },
      {
        score: 6,
        text: 'Dead',
        color: 'black',
      },
      {
        score: -1,
        text: 'N/A',
        color: 'lightgray',
      },
    ];
    const selectedMotion = ref({
      score: currentFrameMotionAnnotationScore.value,
      text: currentFrameMotionAnnotationText.value,
      color: currentFrameMotionAnnotationColor.value,
    });
    watch(currentFrameMotionAnnotationScore, () => {
      selectedMotion.value = {
        score: currentFrameMotionAnnotationScore.value,
        text: currentFrameMotionAnnotationText.value,
        color: currentFrameMotionAnnotationColor.value,
      };
      if (currentFrameMotionAnnotationScore.value === 6) {
        const finalTime = FrameNumberToTime(numberOfFrames.value, frameRate.value);
        editEndMinute.value = finalTime.minutes;
        editEndSecond.value = finalTime.seconds;
        setEndTime(finalTime.minutes, finalTime.seconds);
      }
    });
    watch(currentFrameMotionStartFrame, () => {
      const currentStartTime = FrameNumberToTime(currentFrameMotionStartFrame.value, frameRate.value);
      editStartMinute.value = currentStartTime.minutes;
      editStartSecond.value = currentStartTime.seconds;
    });
    watch(currentFrameMotionEndFrame, () => {
      const currentEndTime = FrameNumberToTime(currentFrameMotionEndFrame.value, frameRate.value);
      editEndMinute.value = currentEndTime.minutes;
      editEndSecond.value = currentEndTime.seconds;
    });

    return {
      isRangeType,
      isIntegerType,
      isSetType,
      frameConfirmed,
      deleteFrame,
      saveFrame,
      studyTypes,
      studyType,
      scoreClasses,
      scoreClassesToValues,
      isScorerOrAnnotator,
      isSupervisor,
      selectMotionClass,
      incrementDiscreteScore,
      setScore,
      setStartTime,
      setEndTime,
      scoreClassesToErrors,
      scoreErrors,
      frameInvalid,
      frameInvalidOrConfirmed,
      frameFlagged,
      showConfirmButton,
      drawingAnnotation,
      annotationsVisible,
      toggleAnnotationsVisible: toggleVisibleMode,
      startDrawingAnnotation,
      flag,
      unflag,
      confirm,
      unconfirm,
      clearScores,
      clearAnnotations,
      dataConsoleOpen,
      toggleDataConsole,
      currentStudyType,
      currentFrameMotionScore,
      currentFrameMotionAnnotationScore,
      currentFrameMotionText,
      currentFrameMotionAnnotationText,
      currentFrameMotionColor,
      currentFrameMotionAnnotationColor,
      items,
      selectedMotion,
      editStartMinute,
      editStartSecond,
      editEndMinute,
      editEndSecond,
      min0Max59Rule,
    };
  },
});
